import { doorSvg, type DoorLook, type DoorStyle } from '../drawing/door';
import { esc, idSource, n, safeHex, shade } from '../drawing/svg';
import {
  CUP_D,
  HOLE_D,
  SCREW_D,
  SCREW_OFFSET,
  SCREW_PITCH,
  handleHoles,
  handleIssues,
  hingeIssues,
  isFlap,
  PIECE_LABEL,
  type HandleProduct,
  type Piece,
} from './drilling';

/**
 * The door builder's two views of a piece, ported from the mockup:
 * the FRONT as the shopper will see it (with the handle drawn exactly where it
 * will be drilled) and the BACK as the fitter will see it, with every hinge cup,
 * screw hole and handle hole at its true position and dimensioned.
 */

const BRASS = '#b08d57';
const BRASS_LIGHT = '#e7cf9f';
const DIM = '#1d211f';
const BLUE = '#2f5f8a';
/** Dimension text colour for the cup-edge note: the palette's brass TEXT colour, which passes contrast. */
const BRASS_TEXT = '#7a5e2f';
const WARN = '#a4573a';

/** The handle drawn at its drilled position, in the piece's own mm coordinates. */
export function handleOverlay(piece: Piece, product: HandleProduct): string {
  const d = piece.handle;
  const holes = handleHoles(d);
  if (!holes.length) return '';
  if (product === 'none') {
    return holes.map(([x, y]) => `<circle cx="${n(x)}" cy="${n(y)}" r="${n(HOLE_D / 2 + 1)}" fill="#2a2a28" opacity=".7"/>`).join('');
  }
  if (d.type === 'single') {
    return `<circle cx="${n(d.x)}" cy="${n(d.y)}" r="15" fill="${BRASS}"/><circle cx="${n(d.x - 4)}" cy="${n(d.y - 4)}" r="5" fill="${BRASS_LIGHT}" opacity=".6"/>`;
  }
  const vertical = d.orient === 'vertical';
  const len = d.centres + (product === 'cup' ? 30 : 36);
  const t = product === 'cup' ? 22 : 13;
  const x = vertical ? d.x - t / 2 : d.x - len / 2;
  const y = vertical ? d.y - len / 2 : d.y - t / 2;
  const w = vertical ? t : len;
  const h = vertical ? len : t;
  return `<rect x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" rx="${n(t / 2)}" fill="${BRASS}"/><rect x="${n(x + (vertical ? 2 : 4))}" y="${n(y + (vertical ? 4 : 2))}" width="${n(vertical ? t * 0.3 : len - 8)}" height="${n(vertical ? len - 8 : t * 0.3)}" rx="${n(t / 4)}" fill="${BRASS_LIGHT}" opacity=".55"/>`;
}

export interface FrontViewOptions {
  style: DoorStyle;
  look?: DoorLook;
  colour: string;
  wood?: boolean;
  product: HandleProduct;
  label?: string;
  idPrefix?: string;
}

/** The front of a piece: the range's door with its own handle drawn where it will be drilled. */
export function frontSvg(piece: Piece, o: FrontViewOptions): string {
  const svg = doorSvg(o.style, o.colour, {
    look: o.look,
    wood: o.wood,
    w: piece.w || 100,
    h: piece.h || 100,
    handle: 'none',
    hinge: piece.hinge.side === 'none' ? 'left' : piece.hinge.side,
    label: o.label ?? `${PIECE_LABEL[piece.kind]}, ${piece.h} by ${piece.w} millimetres, front`,
    idPrefix: o.idPrefix,
  });
  return svg.replace(/<\/svg>$/, `${handleOverlay(piece, o.product)}</svg>`);
}

/** The back of a piece with its real drilling, dimensioned in mm. */
export function backSvg(piece: Piece, o: { colour: string; idPrefix?: string }): string {
  const ids = idSource(o.idPrefix ?? 'back');
  const arrow = ids.next();
  const arrowBlue = ids.next();
  const hinge = piece.hinge;
  const W = Math.max(1, piece.w);
  const H = Math.max(1, piece.h);
  const face = shade(safeHex(o.colour), 0.12);
  const fs = Math.max(16, Math.min(W, H) / 16);
  const pad = fs * 5.5;
  const font = 'font-family="Manrope, system-ui, sans-serif"';
  const issues = [...hingeIssues(piece), ...handleIssues(piece)];
  const holes = handleHoles(piece.handle);
  // Seen from the back, a left-hung door has its hinges on the RIGHT; top stays top.
  const side = hinge.side === 'left' ? 'right' : hinge.side === 'right' ? 'left' : hinge.side;

  const label = `Back of the ${PIECE_LABEL[piece.kind].toLowerCase()}, ${H} by ${W} millimetres. ${
    hinge.side === 'none' ? 'No hinge drilling.' : `${hinge.count} hinge cups at ${hinge.positions.join(', ')} millimetres, ${hinge.edge} from the edge.`
  } ${holes.length ? `${holes.length} handle hole${holes.length > 1 ? 's' : ''}.` : 'No handle holes.'}${issues.length ? ` ${issues.length} drilling issue${issues.length > 1 ? 's' : ''}.` : ''}`;

  let s = `<svg viewBox="${n(-pad)} ${n(-pad)} ${n(W + pad * 2)} ${n(H + pad * 2)}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(label)}">`;
  s += `<defs><marker id="${arrow}" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="${n(fs / 3)}" markerHeight="${n(fs / 3)}" markerUnits="userSpaceOnUse" orient="auto-start-reverse"><path d="M0 0 10 5 0 10z" fill="${DIM}"/></marker><marker id="${arrowBlue}" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="${n(fs / 3)}" markerHeight="${n(fs / 3)}" markerUnits="userSpaceOnUse" orient="auto-start-reverse"><path d="M0 0 10 5 0 10z" fill="${BLUE}"/></marker></defs>`;
  s += `<rect width="${n(W)}" height="${n(H)}" rx="3" fill="${face}" stroke="${shade(face, -0.35)}" stroke-width="${n(Math.max(1.5, fs / 10))}"/>`;

  const dline = (x1: number, y1: number, x2: number, y2: number, text: string, lx: number, ly: number, rotate = false) =>
    `<line x1="${n(x1)}" y1="${n(y1)}" x2="${n(x2)}" y2="${n(y2)}" stroke="${DIM}" stroke-width="${n(fs / 12)}" marker-start="url(#${arrow})" marker-end="url(#${arrow})"/><text x="${n(lx)}" y="${n(ly)}" ${font} font-size="${n(fs)}" font-weight="800" fill="${DIM}" text-anchor="middle"${rotate ? ` transform="rotate(-90 ${n(lx)} ${n(ly)})"` : ''}>${esc(text)}</text>`;
  const ext = (x1: number, y1: number, x2: number, y2: number) =>
    `<line x1="${n(x1)}" y1="${n(y1)}" x2="${n(x2)}" y2="${n(y2)}" stroke="${DIM}" stroke-width="${n(fs / 16)}" stroke-dasharray="${n(fs / 3)} ${n(fs / 4)}" opacity=".55"/>`;

  s += dline(0, H + fs * 1.6, W, H + fs * 1.6, `${W}`, W / 2, H + fs * 1.3);
  const heightDimX = side === 'left' ? W + fs * 1.6 : -fs * 1.6;
  s += dline(heightDimX, 0, heightDimX, H, `${H}`, heightDimX + (side === 'left' ? fs * 0.95 : -fs * 0.45), H / 2, true);

  if (hinge.side !== 'none') {
    const vertical = !isFlap(hinge.side);
    hinge.positions.forEach((p, k) => {
      // Along the edge; "from the left" seen from the FRONT is from the right seen from the back.
      const a = vertical ? p : W - p;
      const cx = vertical ? (side === 'left' ? hinge.edge : W - hinge.edge) : a;
      const cy = vertical ? a : side === 'top' ? hinge.edge : H - hinge.edge;
      s += `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(CUP_D / 2)}" fill="${shade(face, -0.28)}" stroke="${DIM}" stroke-width="${n(fs / 12)}"/><circle cx="${n(cx)}" cy="${n(cy)}" r="${n(CUP_D / 2 - 4)}" fill="${shade(face, -0.4)}"/>`;
      // The two screw holes of the 45/9.5 pattern, set in towards the middle of the door.
      const inward = vertical ? (side === 'left' ? 1 : -1) : side === 'top' ? 1 : -1;
      for (const sign of [-1, 1]) {
        const sx = vertical ? cx + inward * SCREW_OFFSET : cx + (sign * SCREW_PITCH) / 2;
        const sy = vertical ? cy + (sign * SCREW_PITCH) / 2 : cy + inward * SCREW_OFFSET;
        s += `<circle cx="${n(sx)}" cy="${n(sy)}" r="${n(SCREW_D / 2)}" fill="${shade(face, -0.45)}" stroke="${DIM}" stroke-width="${n(fs / 16)}"/>`;
      }
      if (vertical) {
        const dx = side === 'left' ? -fs * 1.4 : W + fs * 1.4;
        const off = (side === 'left' ? -1 : 1) * fs * 1.2 * (k % 2);
        s += ext(cx, cy, dx + off, cy);
        s += dline(dx + off, 0, dx + off, cy, `${p}`, dx + off + (side === 'left' ? -fs * 0.45 : fs * 0.45) * 1.9, cy / 2, true);
      } else {
        const dy = side === 'top' ? -fs * 1.4 - fs * 1.2 * (k % 2) : H + fs * 3.4 + fs * 1.2 * (k % 2);
        s += ext(cx, cy, cx, dy);
        s += dline(W, dy, cx, dy, `${p}`, (W + cx) / 2, dy - fs * 0.35);
      }
    });
    // The cup centre's distance from the edge, on the first hinge.
    const p0 = hinge.positions[0] ?? 0;
    const note = `${hinge.edge} · Ø${CUP_D}`;
    if (vertical) {
      const cx = side === 'left' ? hinge.edge : W - hinge.edge;
      const y0 = p0 + CUP_D;
      s += `<line x1="${n(side === 'left' ? 0 : W)}" y1="${n(y0)}" x2="${n(cx)}" y2="${n(y0)}" stroke="${BRASS_TEXT}" stroke-width="${n(fs / 10)}" marker-start="url(#${arrow})" marker-end="url(#${arrow})"/>`;
      s += `<text x="${n(side === 'left' ? cx + fs * 0.5 : cx - fs * 0.5)}" y="${n(y0 + fs * 0.35)}" ${font} font-size="${n(fs * 0.8)}" font-weight="800" fill="${BRASS_TEXT}" text-anchor="${side === 'left' ? 'start' : 'end'}">${esc(note)}</text>`;
    } else {
      const x0 = W - p0 + CUP_D;
      const cy = side === 'top' ? hinge.edge : H - hinge.edge;
      s += `<line x1="${n(x0)}" y1="${n(side === 'top' ? 0 : H)}" x2="${n(x0)}" y2="${n(cy)}" stroke="${BRASS_TEXT}" stroke-width="${n(fs / 10)}" marker-start="url(#${arrow})" marker-end="url(#${arrow})"/>`;
      s += `<text x="${n(x0 + fs * 0.4)}" y="${n(side === 'top' ? cy + fs : cy - fs * 0.3)}" ${font} font-size="${n(fs * 0.8)}" font-weight="800" fill="${BRASS_TEXT}">${esc(note)}</text>`;
    }
  } else {
    s += `<text x="${n(W / 2)}" y="${n(H - fs * 1.2)}" text-anchor="middle" ${font} font-size="${n(fs * 0.85)}" font-weight="700" fill="${shade(face, -0.55)}">No hinge drilling</text>`;
  }

  // Handle holes go right through, so they show on the back too, mirrored left to right.
  // The handle centre is dimensioned from the edge that is LEFT seen from the front (the right edge here).
  if (holes.length) {
    const d = piece.handle;
    const cx = W - d.x;
    const cy = d.y;
    const pad2 = fs * 0.75;
    const xs = holes.map(([x]) => W - x);
    const ys = holes.map(([, y]) => y);
    s +=
      holes.length === 1
        ? `<circle cx="${n(xs[0] as number)}" cy="${n(ys[0] as number)}" r="${n(fs * 1.1)}" fill="${BLUE}" fill-opacity=".08" stroke="${BLUE}" stroke-width="${n(fs / 12)}" stroke-dasharray="${n(fs / 3)} ${n(fs / 5)}"/>`
        : `<rect x="${n(Math.min(...xs) - pad2)}" y="${n(Math.min(...ys) - pad2)}" width="${n(Math.max(...xs) - Math.min(...xs) + 2 * pad2)}" height="${n(Math.max(...ys) - Math.min(...ys) + 2 * pad2)}" rx="${n(pad2)}" fill="${BLUE}" fill-opacity=".08" stroke="${BLUE}" stroke-width="${n(fs / 12)}" stroke-dasharray="${n(fs / 3)} ${n(fs / 5)}"/>`;
    holes.forEach(([x, y]) => {
      const bx = W - x;
      s += `<circle cx="${n(bx)}" cy="${n(y)}" r="${n(fs * 0.5)}" fill="#fff" stroke="${BLUE}" stroke-width="${n(fs / 10)}"/>`;
      s += `<circle data-hole="handle" cx="${n(bx)}" cy="${n(y)}" r="${n(Math.max(HOLE_D / 2, fs * 0.26))}" fill="${BLUE}"/>`;
    });
    s += `<line x1="${n(W)}" y1="${n(cy)}" x2="${n(cx)}" y2="${n(cy)}" stroke="${BLUE}" stroke-width="${n(fs / 12)}" marker-start="url(#${arrowBlue})" marker-end="url(#${arrowBlue})"/>`;
    s += `<text x="${n((W + cx) / 2)}" y="${n(cy - fs * 0.35)}" text-anchor="middle" ${font} font-size="${n(fs * 0.8)}" font-weight="800" fill="${BLUE}">${esc(d.x)}</text>`;
    s += `<line x1="${n(cx)}" y1="0" x2="${n(cx)}" y2="${n(cy)}" stroke="${BLUE}" stroke-width="${n(fs / 12)}" stroke-dasharray="${n(fs / 3)} ${n(fs / 5)}" marker-start="url(#${arrowBlue})" marker-end="url(#${arrowBlue})"/>`;
    s += `<text x="${n(cx - fs * 0.4)}" y="${n(cy / 2)}" text-anchor="end" ${font} font-size="${n(fs * 0.8)}" font-weight="800" fill="${BLUE}">${esc(d.y)}</text>`;
    if (holes.length === 2) {
      s += `<text x="${n(cx - fs * 0.4)}" y="${n(cy + fs * 1.2)}" text-anchor="end" ${font} font-size="${n(fs * 0.7)}" font-weight="800" fill="${BLUE}">${esc(`${d.centres} c/c`)}</text>`;
    }
  }

  // One banner for every piece: issues first, otherwise what this view shows.
  const banner = issues.length
    ? `⚠ ${issues.length} drilling issue${issues.length > 1 ? 's' : ''}, see the list`
    : `Seen from the back${hinge.side !== 'none' ? ` · hinges on the ${side}` : ''}${holes.length ? ' · handle holes go right through' : ''}`;
  s += `<text x="${n(W / 2)}" y="${n(-pad + fs * 1.3)}" text-anchor="middle" ${font} font-size="${n(fs * 0.85)}" font-weight="700" fill="${issues.length ? WARN : '#4a504c'}">${esc(banner)}</text>`;

  // Legend under the width dimension.
  const ly = H + fs * 3.4;
  let lx = 0;
  if (hinge.side !== 'none') {
    s += `<circle cx="${n(lx + fs * 0.4)}" cy="${n(ly - fs * 0.3)}" r="${n(fs * 0.4)}" fill="${shade(face, -0.28)}" stroke="${DIM}" stroke-width="${n(fs / 14)}"/><text x="${n(lx + fs * 1.1)}" y="${n(ly)}" ${font} font-size="${n(fs * 0.7)}" font-weight="700" fill="#4a504c">${esc(`Hinge cup Ø${CUP_D} + 2 screw holes`)}</text>`;
    lx += fs * 12;
  }
  if (holes.length) {
    s += `<circle cx="${n(lx + fs * 0.4)}" cy="${n(ly - fs * 0.3)}" r="${n(fs * 0.4)}" fill="${BLUE}"/><text x="${n(lx + fs * 1.1)}" y="${n(ly)}" ${font} font-size="${n(fs * 0.7)}" font-weight="700" fill="${BLUE}">${esc(`Handle hole Ø${HOLE_D}, through`)}</text>`;
  }
  return `${s}</svg>`;
}

/** The caption under the back view. */
export function backCaption(piece: Piece): string {
  const holes = handleHoles(piece.handle).length;
  const d = piece.handle;
  const cups = piece.hinge.side === 'none' ? 'no hinges' : `${piece.hinge.count} × Ø${CUP_D} cups at ${piece.hinge.positions.join(', ')}mm`;
  const handle = holes === 0 ? 'no handle holes' : `handle ${holes} × Ø${HOLE_D}${holes === 2 ? ` at ${d.centres} c/c` : ''}, centre ${d.x} / ${d.y}mm`;
  return `${cups} · ${handle}`;
}
